import React, { useState, useEffect, useRef } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { Header } from '@/components/Header';
import { MatterportViewer } from '@/components/MatterportViewer';
import { WelcomeScreen } from '@/components/WelcomeScreen';
import { OnboardingScreen } from '@/components/OnboardingScreen';
import { TourSelectionScreen } from '@/components/TourSelectionScreen';
import { GlobalSearchPanel } from '@/components/GlobalSearchPanel';
import { QuizPanel } from '@/components/QuizPanel';
import { ELearningAdminPanel } from '@/components/ELearningAdminPanel';
import { HotspotTypeManager } from '@/components/CustomHotspots';
import { InfoHotspotCreator, InfoHotspotViewer } from '@/components/InfoHotspotSystem';
import { InfoHotspotsPanel } from '@/components/InfoHotspotsPanel';
import MaintenanceForm from '@/components/MaintenanceForm';
import { MaintenanceEquipmentPanel } from '@/components/MaintenanceEquipmentPanel';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { TourProvider, useTour } from '@/contexts/TourContext';

// Default Model ID (fallback when no tour selected)
const DEFAULT_MODEL_ID = process.env.REACT_APP_MATTERPORT_MODEL_ID || '6szR5xa384b';
const API_URL = process.env.REACT_APP_BACKEND_URL;

function HomePage() {
  const { currentTour, selectTour, tours, fetchTours } = useTour();
  const { isAuthenticated, loading, isAdmin, hasSeenOnboarding, token } = useAuth();
  const [isActiveIssuesPanelOpen, setIsActiveIssuesPanelOpen] = useState(false);
  const [isAssetsPanelOpen, setIsAssetsPanelOpen] = useState(false);
  const [isInstrumentationPanelOpen, setIsInstrumentationPanelOpen] = useState(false);
  const [isBuildingsPanelOpen, setIsBuildingsPanelOpen] = useState(false);
  const [isCompliancePanelOpen, setIsCompliancePanelOpen] = useState(false);
  const [isReferencePanelOpen, setIsReferencePanelOpen] = useState(false);
  const [isWorkflowsPanelOpen, setIsWorkflowsPanelOpen] = useState(false);
  const [isPlannedChangesPanelOpen, setIsPlannedChangesPanelOpen] = useState(false);
  const [isRiskExposurePanelOpen, setIsRiskExposurePanelOpen] = useState(false);
  const [isCompetencyPanelOpen, setIsCompetencyPanelOpen] = useState(false);
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);
  const [isTrainingModulesPanelOpen, setIsTrainingModulesPanelOpen] = useState(false);
  const [isMaintenanceProceduresPanelOpen, setIsMaintenanceProceduresPanelOpen] = useState(false);
  const [isMaintenanceDashboardOpen, setIsMaintenanceDashboardOpen] = useState(false);
  const [maintenanceDashboardTab, setMaintenanceDashboardTab] = useState('overview');
  const [isMaintenanceEquipmentOpen, setIsMaintenanceEquipmentOpen] = useState(false);
  const [isQuizPanelOpen, setIsQuizPanelOpen] = useState(false);
  const [isELearningAdminOpen, setIsELearningAdminOpen] = useState(false);
  const [isHotspotManagerOpen, setIsHotspotManagerOpen] = useState(false);
  const [isHotspotPlacementOpen, setIsHotspotPlacementOpen] = useState(false);
  const [isInfoHotspotsPanelOpen, setIsInfoHotspotsPanelOpen] = useState(false);
  const [editingHotspot, setEditingHotspot] = useState(null);
  
  // Info hotspots for training
  const [infoHotspots, setInfoHotspots] = useState([]);
  const [selectedInfoHotspot, setSelectedInfoHotspot] = useState(null);
  
  // Areas/Zones for 3D labels
  const [areas, setAreas] = useState([]);
  
  // SDK instance for hotspot placement
  const [sdkInstance, setSdkInstance] = useState(null);
  
  // createTag function from useMatterport hook (ensures state updates)
  // Using ref instead of state because storing a function in state causes issues
  const createTagFnRef = useRef(null);
  
  // Interactive Quiz state (for 3D quizzes)
  const [activeInteractiveQuiz, setActiveInteractiveQuiz] = useState(null);
  
  // Pointer intersection for quiz position capture
  const [pointerIntersection, setPointerIntersection] = useState(null);
  const [currentSweepId, setCurrentSweepId] = useState(null);
  const [cameraPosition, setCameraPosition] = useState(null);
  
  // Debug: log when sweep changes
  useEffect(() => {
    console.log('App.js currentSweepId changed:', currentSweepId);
  }, [currentSweepId]);
  
  // Preview mode - admin can see what client sees
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  // Track if user has selected a tour in this session - reset on every login
  const [hasTourSelected, setHasTourSelected] = useState(false);
  // Track if onboarding is complete for this session
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  
  // Reference to navigate function from MatterportViewer
  const [navigateToSweep, setNavigateToSweep] = useState(null);
  const [navigateToTag, setNavigateToTag] = useState(null);
  const [navigateToPosition, setNavigateToPosition] = useState(null);
  
  // Reset tour selection state when user changes (login/logout)
  useEffect(() => {
    setHasTourSelected(false);
    setOnboardingComplete(false);
    setIsPreviewMode(false);
  }, [isAuthenticated]);
  
  // For admin users, auto-select first tour if none selected
  useEffect(() => {
    if (isAdmin && isAuthenticated && token && !currentTour) {
      // Fetch tours and select the first one
      fetchTours().then(() => {
        // Tours will be fetched, we'll select in the next effect
      });
    }
  }, [isAdmin, isAuthenticated, token, currentTour, fetchTours]);
  
  // Once tours are loaded, select the first one for admin users
  useEffect(() => {
    if (isAdmin && isAuthenticated && tours.length > 0 && !currentTour) {
      console.log('Admin auto-selecting first tour:', tours[0].name);
      selectTour(tours[0]);
    }
  }, [isAdmin, isAuthenticated, tours, currentTour, selectTour]);
  
  // Use the current tour's model ID if available, otherwise use default
  const modelId = currentTour?.matterport_model_id || DEFAULT_MODEL_ID;
  
  // Load info hotspots when tour changes
  useEffect(() => {
    const loadInfoHotspots = async () => {
      if (!currentTour?.id || !token) {
        setInfoHotspots([]);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/info-hotspots?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const hotspots = await response.json();
          console.log('Loaded info hotspots:', hotspots.length);
          setInfoHotspots(hotspots);
        }
      } catch (err) {
        console.warn('Failed to load info hotspots:', err);
      }
    };
    
    loadInfoHotspots();
  }, [currentTour?.id, token]);
  
  // Create visual markers for loaded info hotspots when SDK is ready
  useEffect(() => {
    const createVisualMarkers = async () => {
      console.log('createVisualMarkers called:', {
        sdkInstance: !!sdkInstance,
        infoHotspotsCount: infoHotspots?.length,
        createTagFnAvailable: !!createTagFnRef.current
      });
      
      if (!sdkInstance || !infoHotspots || infoHotspots.length === 0) return;
      if (!createTagFnRef.current) {
        console.log('createTagFn not ready yet, will retry...');
        return;
      }
      
      console.log('Recreating visual markers for', infoHotspots.length, 'info hotspots');
      
      for (const hotspot of infoHotspots) {
        try {
          // Get the icon emoji if available
          const iconEmoji = hotspot.icon?.emoji || '📍';
          const stemHeight = hotspot.stem_height || 0.15;
          
          console.log('Creating info hotspot tag:', hotspot.label, 'with emoji:', iconEmoji, 'stem:', stemHeight);
          
          // Use the new createInfoHotspotTag function that supports emoji icons
          const tagId = await createTagFnRef.current({
            label: hotspot.label,
            emoji: iconEmoji,
            position: hotspot.position,
            stemVector: { x: 0, y: stemHeight, z: 0 },
            color: hotspot.color || { r: 0.77, g: 0.64, b: 0.29 },
          });
          
          console.log('createTagFnRef.current returned:', tagId);
          
          if (tagId) {
            console.log('Created visual marker for info hotspot:', hotspot.label, '->', tagId);
            
            // Update the hotspot with the new matterport tag ID (it changes each session)
            if (tagId !== hotspot.matterport_tag_id) {
              await fetch(`${API_URL}/api/info-hotspots/${hotspot.id}/matterport-id?matterport_tag_id=${tagId}`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              // Update local state with new tag ID
              setInfoHotspots(prev => prev.map(h => 
                h.id === hotspot.id ? { ...h, matterport_tag_id: tagId } : h
              ));
            }
          }
        } catch (err) {
          console.warn('Failed to create visual marker for hotspot:', hotspot.label, err);
        }
      }
    };
    
    // Delay to ensure SDK and createTagFn are ready
    const timer = setTimeout(createVisualMarkers, 2500);
    return () => clearTimeout(timer);
  }, [sdkInstance, infoHotspots.length, token]); // Use length to avoid re-running on tag ID updates
  
  // Load areas/zones when tour changes
  useEffect(() => {
    const loadAreas = async () => {
      if (!currentTour?.id || !token) {
        setAreas([]);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/areas/tour/${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const areasData = await response.json();
          console.log('Loaded areas:', areasData.length);
          setAreas(areasData);
        }
      } catch (err) {
        console.warn('Failed to load areas:', err);
      }
    };
    
    loadAreas();
  }, [currentTour?.id, token]);
  
  // Create SDK tags for areas - using same method as info hotspots
  useEffect(() => {
    const createAreaTags = async () => {
      console.log('createAreaTags check:', { 
        sdkInstance: !!sdkInstance, 
        areasCount: areas?.length,
        createTagFn: !!createTagFnRef.current 
      });
      
      if (!sdkInstance || !areas || areas.length === 0) return;
      if (!createTagFnRef.current) {
        console.log('createTagFn not ready for areas');
        return;
      }
      
      console.log('Creating area tags for', areas.length, 'areas');
      
      for (let i = 0; i < areas.length; i++) {
        const area = areas[i];
        if (!area.position) {
          console.log('Skipping area without position:', area.name);
          continue;
        }
        
        console.log(`Creating tag ${i+1}/${areas.length} for area:`, area.name, 'at', area.position);
        
        try {
          // Convert hex color to RGB
          const hex = area.color || '#FFF200'; // Default to safety yellow
          const r = parseInt(hex.slice(1, 3), 16) / 255;
          const g = parseInt(hex.slice(3, 5), 16) / 255;
          const b = parseInt(hex.slice(5, 7), 16) / 255;
          
          const tagId = await createTagFnRef.current({
            label: `🏢 ${area.name}`,
            emoji: '🏢',
            position: area.position,
            stemVector: { x: 0, y: 1.0, z: 0 }, // Tall stem - 1 meter
            color: { r, g, b },
          });
          
          console.log('Created area tag:', area.name, '-> tagId:', tagId);
          
          // Small delay between tag creations to avoid SDK issues
          await new Promise(resolve => setTimeout(resolve, 200));
        } catch (err) {
          console.error('Failed to create area tag:', area.name, err);
        }
      }
      
      console.log('Finished creating area tags');
    };
    
    const timer = setTimeout(createAreaTags, 5000);
    return () => clearTimeout(timer);
  }, [sdkInstance, areas]);
  
  // Debug: Log when modelId changes
  useEffect(() => {
    console.log('HomePage: currentTour changed:', currentTour?.name, 'modelId:', modelId);
  }, [currentTour, modelId]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <div className="flex items-center justify-center mx-auto mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_7a888182-d9ad-4911-af25-412e3d52e004/artifacts/sstxximv_Zentara%20As-Ltd._-01.png" 
              alt="Zentara" 
              className="h-16 w-auto object-contain"
            />
          </div>
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show welcome screen for unauthenticated users
  if (!isAuthenticated) {
    return <WelcomeScreen />;
  }

  // For non-admin users: Show onboarding if they haven't seen it
  if (!isAdmin && !hasSeenOnboarding && !onboardingComplete) {
    return <OnboardingScreen onComplete={() => setOnboardingComplete(true)} />;
  }

  // For non-admin users: Always show tour selection screen first (client portal)
  // hasTourSelected tracks if user has made a selection THIS session
  if (!isAdmin && !hasTourSelected) {
    return (
      <TourSelectionScreen 
        onSelectTour={(tour) => {
          selectTour(tour);
          setHasTourSelected(true);
        }} 
      />
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      <Header 
        onOpenActiveIssues={() => setIsActiveIssuesPanelOpen(true)} 
        onOpenAssets={() => setIsAssetsPanelOpen(true)}
        onOpenInstrumentation={() => setIsInstrumentationPanelOpen(true)}
        onOpenBuildings={() => setIsBuildingsPanelOpen(true)}
        onOpenCompliance={() => setIsCompliancePanelOpen(true)}
        onOpenReference={() => setIsReferencePanelOpen(true)}
        onOpenWorkflows={() => setIsWorkflowsPanelOpen(true)}
        onOpenPlannedChanges={() => setIsPlannedChangesPanelOpen(true)}
        onOpenRiskExposure={() => setIsRiskExposurePanelOpen(true)}
        onOpenCompetency={() => setIsCompetencyPanelOpen(true)}
        onOpenGlobalSearch={() => setIsGlobalSearchOpen(true)}
        onOpenTrainingModules={() => setIsTrainingModulesPanelOpen(true)}
        onOpenMaintenanceProcedures={() => setIsMaintenanceProceduresPanelOpen(true)}
        onOpenMaintenanceDashboard={() => setIsMaintenanceDashboardOpen(true)}
        onOpenMaintenanceEquipment={() => setIsMaintenanceEquipmentOpen(true)}
        onOpenQuizzes={() => setIsQuizPanelOpen(true)}
        onOpenELearningAdmin={() => setIsELearningAdminOpen(true)}
        onOpenHotspotManager={() => setIsHotspotManagerOpen(true)}
        onOpenHotspotPlacement={() => setIsHotspotPlacementOpen(true)}
        onOpenInfoHotspots={() => setIsInfoHotspotsPanelOpen(true)}
        isPreviewMode={isPreviewMode}
        onTogglePreviewMode={() => setIsPreviewMode(!isPreviewMode)}
      />
      <main className="flex-1 min-h-0 p-4">
        <MatterportViewer 
          modelId={modelId} 
          isActiveIssuesPanelOpen={isActiveIssuesPanelOpen}
          onCloseActiveIssuesPanel={() => setIsActiveIssuesPanelOpen(false)}
          onOpenActiveIssuesPanel={() => setIsActiveIssuesPanelOpen(true)}
          isAssetsPanelOpen={isAssetsPanelOpen}
          onCloseAssetsPanel={() => setIsAssetsPanelOpen(false)}
          isInstrumentationPanelOpen={isInstrumentationPanelOpen}
          onCloseInstrumentationPanel={() => setIsInstrumentationPanelOpen(false)}
          isBuildingsPanelOpen={isBuildingsPanelOpen}
          onCloseBuildingsPanel={() => setIsBuildingsPanelOpen(false)}
          onOpenBuildingsPanel={() => setIsBuildingsPanelOpen(true)}
          isCompliancePanelOpen={isCompliancePanelOpen}
          onCloseCompliancePanel={() => setIsCompliancePanelOpen(false)}
          isReferencePanelOpen={isReferencePanelOpen}
          onCloseReferencePanel={() => setIsReferencePanelOpen(false)}
          isWorkflowsPanelOpen={isWorkflowsPanelOpen}
          onCloseWorkflowsPanel={() => setIsWorkflowsPanelOpen(false)}
          isPlannedChangesPanelOpen={isPlannedChangesPanelOpen}
          onClosePlannedChangesPanel={() => setIsPlannedChangesPanelOpen(false)}
          onOpenPlannedChangesPanel={() => setIsPlannedChangesPanelOpen(true)}
          isRiskExposurePanelOpen={isRiskExposurePanelOpen}
          onCloseRiskExposurePanel={() => setIsRiskExposurePanelOpen(false)}
          onOpenRiskExposurePanel={() => setIsRiskExposurePanelOpen(true)}
          isCompetencyPanelOpen={isCompetencyPanelOpen}
          onCloseCompetencyPanel={() => setIsCompetencyPanelOpen(false)}
          isTrainingModulesPanelOpen={isTrainingModulesPanelOpen}
          onCloseTrainingModulesPanel={() => setIsTrainingModulesPanelOpen(false)}
          onOpenTrainingModulesPanel={() => setIsTrainingModulesPanelOpen(true)}
          isMaintenanceProceduresPanelOpen={isMaintenanceProceduresPanelOpen}
          onCloseMaintenanceProceduresPanel={() => setIsMaintenanceProceduresPanelOpen(false)}
          onOpenMaintenanceProceduresPanel={() => setIsMaintenanceProceduresPanelOpen(true)}
          isMaintenanceDashboardOpen={isMaintenanceDashboardOpen}
          onCloseMaintenanceDashboard={() => setIsMaintenanceDashboardOpen(false)}
          onOpenMaintenanceDashboard={(tab) => {
            if (tab) setMaintenanceDashboardTab(tab);
            setIsMaintenanceDashboardOpen(true);
          }}
          maintenanceDashboardTab={maintenanceDashboardTab}
          onNavigateReady={(fn) => setNavigateToSweep(() => fn)}
          onNavigateToTagReady={(fn) => setNavigateToTag(() => fn)}
          onNavigateToPositionReady={(fn) => setNavigateToPosition(() => fn)}
          isPreviewMode={isPreviewMode}
          activeInteractiveQuiz={activeInteractiveQuiz}
          onCloseInteractiveQuiz={() => setActiveInteractiveQuiz(null)}
          onPointerIntersectionChange={setPointerIntersection}
          onCurrentSweepChange={setCurrentSweepId}
          onCameraPositionChange={setCameraPosition}
          onSdkInstanceChange={setSdkInstance}
          onRequestCreateTag={(fn) => { createTagFnRef.current = fn; }}
          infoHotspots={infoHotspots}
          onInfoHotspotClick={(hotspot) => setSelectedInfoHotspot(hotspot)}
        />
      </main>
      
      {/* Global Search Panel */}
      <GlobalSearchPanel
        isOpen={isGlobalSearchOpen}
        onClose={() => setIsGlobalSearchOpen(false)}
        onNavigate={navigateToSweep}
      />
      
      {/* Quiz Panel */}
      <QuizPanel
        tourId={currentTour?.id}
        isOpen={isQuizPanelOpen}
        onClose={() => setIsQuizPanelOpen(false)}
        onStartInteractiveQuiz={(quiz) => {
          console.log('App.js: Starting interactive quiz:', quiz?.title);
          setActiveInteractiveQuiz(quiz);
          setIsQuizPanelOpen(false);
        }}
        pointerIntersection={pointerIntersection}
        currentSweepId={currentSweepId}
        cameraPosition={cameraPosition}
      />
      
      {/* E-Learning Admin Panel */}
      <ELearningAdminPanel
        isOpen={isELearningAdminOpen}
        onClose={() => setIsELearningAdminOpen(false)}
      />
      
      {/* Hotspot Type Manager */}
      <HotspotTypeManager
        tourId={currentTour?.id}
        isOpen={isHotspotManagerOpen}
        onClose={() => setIsHotspotManagerOpen(false)}
      />
      
      {/* Info Hotspot Creator - Learning Hotspots with Image Upload */}
      <InfoHotspotCreator
        isOpen={isHotspotPlacementOpen || !!editingHotspot}
        onClose={() => {
          setIsHotspotPlacementOpen(false);
          setEditingHotspot(null);
        }}
        pointerIntersection={pointerIntersection}
        currentSweepId={currentSweepId}
        cameraPosition={cameraPosition}
        tourId={currentTour?.id}
        createTagFn={(tagData) => createTagFnRef.current?.(tagData)}
        editingHotspot={editingHotspot}
        onHotspotCreated={(hotspot) => {
          console.log('Info hotspot created:', hotspot);
          setInfoHotspots(prev => [...prev, hotspot]);
        }}
        onHotspotUpdated={(updatedHotspot) => {
          console.log('Info hotspot updated:', updatedHotspot);
          setInfoHotspots(prev => prev.map(h => 
            h.id === updatedHotspot.id ? updatedHotspot : h
          ));
          setEditingHotspot(null);
        }}
      />
      
      {/* Info Hotspot Viewer - Shows when user clicks on a hotspot */}
      <InfoHotspotViewer
        hotspot={selectedInfoHotspot}
        isOpen={!!selectedInfoHotspot}
        onClose={() => setSelectedInfoHotspot(null)}
        navigateToSweep={navigateToSweep}
      />
      
      {/* Info Hotspots Panel - View and manage all info hotspots */}
      <InfoHotspotsPanel
        isOpen={isInfoHotspotsPanelOpen}
        onClose={() => setIsInfoHotspotsPanelOpen(false)}
        infoHotspots={infoHotspots}
        onRefresh={async () => {
          if (!currentTour?.id || !token) return;
          try {
            const response = await fetch(`${API_URL}/api/info-hotspots?tour_id=${currentTour.id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const hotspots = await response.json();
              setInfoHotspots(hotspots);
            }
          } catch (err) {
            console.warn('Failed to refresh info hotspots:', err);
          }
        }}
        onViewHotspot={(hotspot) => {
          setSelectedInfoHotspot(hotspot);
          // Keep panel open - don't close it
        }}
        onEditHotspot={(hotspot) => {
          setEditingHotspot(hotspot);
          setIsInfoHotspotsPanelOpen(false); // Close panel for edit since dialog opens
        }}
        onDeleteHotspot={(hotspotId) => {
          setInfoHotspots(prev => prev.filter(h => h.id !== hotspotId));
          // Keep panel open after delete
        }}
        onNavigateToHotspot={async (hotspot) => {
          console.log('Navigating to hotspot:', hotspot.label);
          console.log('Hotspot data:', { sweep_id: hotspot.sweep_id, camera_rotation: hotspot.camera_rotation, position: hotspot.position, tag_id: hotspot.matterport_tag_id });
          
          // Navigate to the sweep where the hotspot was created
          // This is the most reliable since sweep_id is saved when hotspot is created
          if (hotspot.sweep_id && navigateToSweep) {
            try {
              const navOptions = { 
                transition: 'fly',
                transitionTime: 1500 
              };
              
              // Use saved camera rotation if available
              if (hotspot.camera_rotation) {
                navOptions.rotation = hotspot.camera_rotation;
                console.log('Using saved camera rotation:', hotspot.camera_rotation);
              }
              
              await navigateToSweep(hotspot.sweep_id, navOptions);
              console.log('Navigated to sweep:', hotspot.sweep_id);
            } catch (err) {
              console.warn('Sweep navigation failed:', err);
            }
          }
          
          // Show the dialog after navigation
          setTimeout(() => {
            setSelectedInfoHotspot(hotspot);
          }, 1600);
        }}
      />
      
      {/* Maintenance Equipment Panel */}
      <MaintenanceEquipmentPanel
        isOpen={isMaintenanceEquipmentOpen}
        onClose={() => setIsMaintenanceEquipmentOpen(false)}
        currentSweepId={currentSweepId}
        pointerIntersection={pointerIntersection}
      />
    </div>
  );
}

// Shared tour view (public access via share token)
function SharedTourPage() {
  const { shareToken } = useParams();
  const { currentTour, loadSharedTour } = useTour();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTour = async () => {
      if (shareToken) {
        const result = await loadSharedTour(shareToken);
        if (!result.success) {
          setError(result.error);
        }
        setLoading(false);
      }
    };
    loadTour();
  }, [shareToken, loadSharedTour]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading tour...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center">
          <p className="text-destructive text-lg mb-2">Tour not found</p>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const modelId = currentTour?.matterport_model_id || DEFAULT_MODEL_ID;

  return (
    <div className="flex flex-col h-screen bg-background">
      <Header isSharedView />
      <main className="flex-1 min-h-0 p-4">
        <MatterportViewer modelId={modelId} />
      </main>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <TourProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/share/:shareToken" element={<SharedTourPage />} />
              <Route path="/maintenance/:equipmentId" element={<MaintenanceForm />} />
              <Route path="*" element={<HomePage />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="bottom-right" />
        </TourProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
